import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import type { ContractType } from '../api/types';
import { WizardProgress } from '../components/DesignSystem';
import { PageHeader } from '../components/PageHeader';
import { StepPrimaryTerms } from './upload/StepPrimaryTerms';
import { StepReviewApprove } from './upload/StepReviewApprove';
import { StepReviewShares } from './upload/StepReviewShares';
import { StepRightsInvoicing } from './upload/StepRightsInvoicing';
import type { ManualReviewReason } from './upload/manualReview';
import { useInferWizardStep } from './upload/useInferWizardStep';

const STEP_TITLES = [
  'Primary Terms',
  'Show, Currency & Shares',
  'Rights & Invoicing',
  'Review & Approve',
] as const;

interface WizardState {
  step: 1 | 2 | 3 | 4;
  contractId: string | null;
  contractType: ContractType;
  /** Why the user needs to review or enter terms manually after extraction. */
  manualReviewReason: ManualReviewReason | null;
}

const INITIAL_STATE: WizardState = {
  step: 1,
  contractId: null,
  contractType: 'primary',
  manualReviewReason: null,
};

/** `/upload` (fresh start) or `/upload/:contractId` (resume) — 4-step contract upload wizard. */
export function UploadContractPage() {
  const navigate = useNavigate();
  const { contractId: routeContractId } = useParams<{ contractId?: string }>();
  const [state, setState] = useState<WizardState>(INITIAL_STATE);
  const [userNavigated, setUserNavigated] = useState(false);

  const inferred = useInferWizardStep(routeContractId);

  useEffect(() => {
    if (inferred.redirectTo !== null) {
      navigate(inferred.redirectTo, { replace: true });
    }
  }, [inferred.redirectTo, navigate]);

  const isResuming = routeContractId !== undefined && !userNavigated;
  const contractId = routeContractId ?? state.contractId;
  const step = isResuming ? inferred.step : state.step;

  function goToStep(nextStep: 1 | 2 | 3 | 4) {
    setUserNavigated(true);
    setState((current) => ({
      ...current,
      step: nextStep,
      contractId: contractId ?? current.contractId,
    }));
  }

  if (isResuming && (inferred.isLoading || inferred.redirectTo !== null)) {
    return (
      <>
        <PageHeader
          caption="Upload your contract document for AI-powered term extraction."
          title="Upload Contract"
        />
        <p className="rfp-loading">Loading your upload…</p>
      </>
    );
  }

  const stepTitle = step === null ? '' : STEP_TITLES[step - 1];

  return (
    <>
      <PageHeader
        caption="Upload your contract document for AI-powered term extraction."
        title="Upload Contract"
      />

      <div className="rfp-wizard-head">
        <p className="rfp-step-label">
          — Step {step ?? 1} of 4 / {stepTitle}
        </p>
        <WizardProgress current={step ?? 1} steps={4} />
      </div>

      {step === 1 && (
        <StepPrimaryTerms
          onComplete={(newContractId, contractType, manualReviewReason) => {
            setUserNavigated(true);
            setState({ step: 2, contractId: newContractId, contractType, manualReviewReason });
            navigate(`/upload/${newContractId}`, { replace: true });
          }}
        />
      )}

      {step === 2 && contractId !== null && contractId !== undefined && (
        <StepReviewShares
          contractId={contractId}
          manualReviewReason={state.manualReviewReason}
          onBack={() => goToStep(1)}
          onNext={() => goToStep(3)}
        />
      )}

      {step === 3 && contractId !== null && contractId !== undefined && (
        <StepRightsInvoicing
          contractId={contractId}
          onBack={() => goToStep(2)}
          onNext={() => goToStep(4)}
        />
      )}

      {step === 4 && contractId !== null && contractId !== undefined && (
        <StepReviewApprove
          contractId={contractId}
          onAuthorized={() => navigate('/authorised-contracts')}
          onBack={() => goToStep(3)}
        />
      )}
    </>
  );
}
